import { useEffect, useState } from "react";

/**
 * Cycle through a list of loading messages while `active` is true. Returns the
 * currently displayed string. Resets to the first message whenever `active`
 * flips from false → true so the sequence always starts at the beginning.
 *
 * Built for the Sutherland-school UX-latency principle: long-running AI
 * responses feel shorter when the loading state speaks to the user instead of
 * showing a generic spinner.
 *
 * @param {string[]} messages   list of messages to cycle through
 * @param {boolean}  active     whether the cycle should be running
 * @param {number}   intervalMs how often to advance (default 1800 ms)
 */
const useRotatingMessages = (messages, active, intervalMs = 1800) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active || !messages || messages.length === 0) {
      setIndex(0);
      return undefined;
    }
    setIndex(0);
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % messages.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, messages, intervalMs]);

  if (!messages || messages.length === 0) return "";
  return messages[index] || messages[0];
};

export default useRotatingMessages;
